import { useInfiniteQuery, useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import { notificationKeys } from '@/features/notifications/hooks/useNotifications';
import { taskService } from '@/features/tasks/services/taskService';
import { compareTasksByAssignmentOrder } from '@/features/tasks/utils/applyTaskFilters';
import { markJustCopiedTask } from '@/features/tasks/utils/justCopiedTasks';
import { useAuth } from '@/store/AuthContext';
import type {
  CreateCommentInput,
  CreateRecurringTaskInput,
  CreateTaskInput,
  ListTasksParams,
  PaginatedTasks,
  Task,
  UpdateCommentInput,
  UpdateRecurringTaskInput,
  UpdateTaskInput,
} from '@/types/task';

export const taskKeys = {
  all: ['tasks'] as const,
  list: (params: ListTasksParams) => [...taskKeys.all, 'list', params] as const,
  infinite: (params: Omit<ListTasksParams, 'page'>) =>
    [...taskKeys.all, 'infinite', params] as const,
  detail: (taskId: string) => [...taskKeys.all, 'detail', taskId] as const,
  attachments: (taskId: string) => [...taskKeys.all, 'attachments', taskId] as const,
  dashboard: (params: ListTasksParams = {}) => [...taskKeys.all, 'dashboard', params] as const,
  recurring: () => [...taskKeys.all, 'recurring'] as const,
};

function invalidateNotifications(queryClient: ReturnType<typeof useQueryClient>) {
  // Worker may lag slightly after task actions — one follow-up refresh is enough
  void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
  window.setTimeout(() => {
    void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
  }, 1500);
}

/** Match api-admin: Assign to me → Self Assign → Assign by me (admin included). */
function insertTaskSorted(
  tasks: Task[],
  createdTask: Task,
  userId: string,
  employeeRef: string | undefined,
): Task[] {
  if (tasks.some((task) => task.id === createdTask.id)) return tasks;
  const next = [...tasks, createdTask];
  next.sort((a, b) => compareTasksByAssignmentOrder(a, b, userId, employeeRef));
  return next;
}

function insertTaskIntoLists(
  queryClient: ReturnType<typeof useQueryClient>,
  createdTask: Task,
  userId: string | undefined,
  employeeRef: string | undefined,
) {
  if (!userId) return;

  for (const [queryKey, current] of queryClient.getQueriesData<PaginatedTasks>({
    queryKey: [...taskKeys.all, 'list'],
  })) {
    if (!current) continue;
    const data = insertTaskSorted(current.data, createdTask, userId, employeeRef);
    if (data === current.data) continue;
    queryClient.setQueryData(queryKey, {
      ...current,
      data,
      total: current.total + 1,
    });
  }

  for (const [queryKey, current] of queryClient.getQueriesData<InfiniteData<PaginatedTasks>>({
    queryKey: [...taskKeys.all, 'infinite'],
  })) {
    if (!current?.pages?.length) continue;
    if (current.pages.some((page) => page.data.some((task) => task.id === createdTask.id))) {
      continue;
    }
    const first = current.pages[0];
    if (!first) continue;
    const data = insertTaskSorted(first.data, createdTask, userId, employeeRef);
    queryClient.setQueryData(queryKey, {
      ...current,
      pages: [
        {
          ...first,
          data,
          total: first.total + 1,
        },
        ...current.pages.slice(1),
      ],
    });
  }
}

async function refreshTaskQueries(queryClient: ReturnType<typeof useQueryClient>) {
  await queryClient.invalidateQueries({ queryKey: taskKeys.all, refetchType: 'active' });
}

export function useTasksQuery(params: ListTasksParams = { page: 1, limit: 100 }) {
  return useQuery({
    queryKey: taskKeys.list(params),
    queryFn: () => taskService.listTasks(params),
  });
}

export function useInfiniteTasksQuery(params: Omit<ListTasksParams, 'page'>) {
  return useInfiniteQuery({
    queryKey: taskKeys.infinite(params),
    queryFn: ({ pageParam }) => taskService.listTasks({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useDashboardStatsQuery(params: ListTasksParams = {}) {
  return useQuery({
    queryKey: taskKeys.dashboard(params),
    queryFn: () => taskService.getDashboardStats(params),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useCreateTaskMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (payload: CreateTaskInput) => taskService.createTask(payload),
    onSuccess: async (createdTask) => {
      insertTaskIntoLists(
        queryClient,
        createdTask,
        user?.id,
        user?.employeeRef,
      );
      await refreshTaskQueries(queryClient);
      invalidateNotifications(queryClient);
    },
  });
}

export function useTaskQuery(taskId: string | null | undefined) {
  return useQuery({
    queryKey: taskKeys.detail(taskId ?? ''),
    queryFn: () => taskService.getTask(taskId!),
    enabled: Boolean(taskId),
  });
}

export function useUpdateTaskMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: UpdateTaskInput }) =>
      taskService.updateTask(taskId, payload),
    onSuccess: async (updatedTask, variables) => {
      queryClient.setQueryData(taskKeys.detail(variables.taskId), updatedTask);
      queryClient.setQueriesData<PaginatedTasks>(
        { queryKey: [...taskKeys.all, 'list'] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            data: current.data.map((task) =>
              task.id === updatedTask.id ? { ...task, ...updatedTask } : task,
            ),
          };
        },
      );
      queryClient.setQueriesData<InfiniteData<PaginatedTasks>>(
        { queryKey: [...taskKeys.all, 'infinite'] },
        (current) => {
          if (!current?.pages?.length) return current;
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              data: page.data.map((task) =>
                task.id === updatedTask.id ? { ...task, ...updatedTask } : task,
              ),
            })),
          };
        },
      );
      await refreshTaskQueries(queryClient);
      invalidateNotifications(queryClient);
    },
  });
}

export function useAddCommentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: CreateCommentInput }) =>
      taskService.addComment(taskId, payload),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: taskKeys.detail(variables.taskId),
        refetchType: 'active',
      });
      invalidateNotifications(queryClient);
    },
  });
}

export function useUpdateCommentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      commentId,
      payload,
    }: {
      taskId: string;
      commentId: string;
      payload: UpdateCommentInput;
    }) => taskService.updateComment(taskId, commentId, payload),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: taskKeys.detail(variables.taskId),
        refetchType: 'active',
      });
    },
  });
}

export function useDeleteCommentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, commentId }: { taskId: string; commentId: string }) =>
      taskService.deleteComment(taskId, commentId),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: taskKeys.detail(variables.taskId),
        refetchType: 'active',
      });
    },
  });
}

export function useToggleCommentReactionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      commentId,
      emoji,
    }: {
      taskId: string;
      commentId: string;
      emoji: string;
    }) => taskService.toggleCommentReaction(taskId, commentId, emoji),
    onSuccess: async (updatedComment, variables) => {
      queryClient.setQueryData<Task | undefined>(taskKeys.detail(variables.taskId), (current) => {
        if (!current?.comments) return current;
        return {
          ...current,
          comments: current.comments.map((comment) =>
            comment.id === updatedComment.id ? { ...comment, ...updatedComment } : comment,
          ),
        };
      });
      await queryClient.invalidateQueries({
        queryKey: taskKeys.detail(variables.taskId),
        refetchType: 'active',
      });
    },
  });
}

export function useDeleteTaskMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => taskService.deleteTask(taskId),
    onSuccess: async (_data, taskId) => {
      queryClient.removeQueries({ queryKey: taskKeys.detail(taskId) });
      await refreshTaskQueries(queryClient);
      invalidateNotifications(queryClient);
    },
  });
}

export function useCopyTaskMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: ({
      taskId,
      includeSubtasks,
    }: {
      taskId: string;
      includeSubtasks?: boolean;
    }) => taskService.copyTask(taskId, { includeSubtasks }),
    onSuccess: async (createdTask) => {
      markJustCopiedTask(createdTask.id);
      queryClient.setQueryData(taskKeys.detail(createdTask.id), createdTask);
      insertTaskIntoLists(
        queryClient,
        createdTask,
        user?.id,
        user?.employeeRef,
      );
      await refreshTaskQueries(queryClient);
      invalidateNotifications(queryClient);
    },
  });
}

export function useCreateRecurringTaskMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (payload: CreateRecurringTaskInput) =>
      taskService.createRecurringTask(payload),
    onSuccess: async (result) => {
      for (const task of result.createdTasks ?? []) {
        insertTaskIntoLists(
        queryClient,
        task,
        user?.id,
        user?.employeeRef,
      );
      }
      await Promise.all([
        refreshTaskQueries(queryClient),
        queryClient.invalidateQueries({ queryKey: taskKeys.recurring() }),
        queryClient.invalidateQueries({ queryKey: [...taskKeys.all, 'dashboard'] }),
      ]);
      invalidateNotifications(queryClient);
    },
  });
}

export function useRecurringTasksQuery(enabled = true) {
  return useQuery({
    queryKey: taskKeys.recurring(),
    queryFn: () => taskService.listRecurringTasks(),
    enabled,
  });
}

export function useUpdateRecurringTaskMutation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: ({
      ruleId,
      payload,
    }: {
      ruleId: string;
      payload: UpdateRecurringTaskInput;
    }) => taskService.updateRecurringTask(ruleId, payload),
    onSuccess: async (result) => {
      for (const task of result.createdTasks ?? []) {
        insertTaskIntoLists(
        queryClient,
        task,
        user?.id,
        user?.employeeRef,
      );
      }
      await Promise.all([
        refreshTaskQueries(queryClient),
        queryClient.invalidateQueries({ queryKey: taskKeys.recurring() }),
        queryClient.invalidateQueries({ queryKey: [...taskKeys.all, 'dashboard'] }),
      ]);
      invalidateNotifications(queryClient);
    },
  });
}

export function useDeleteRecurringTaskMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ruleId: string) => taskService.deleteRecurringTask(ruleId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: taskKeys.recurring() });
    },
  });
}

export function useTaskAttachmentsQuery(taskId: string | null | undefined) {
  return useQuery({
    queryKey: taskKeys.attachments(taskId ?? ''),
    queryFn: () => taskService.listAttachments(taskId!),
    enabled: Boolean(taskId),
  });
}

export function useUploadTaskAttachmentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, file }: { taskId: string; file: File }) =>
      taskService.uploadAttachment(taskId, file),
    onSuccess: async (_attachment, variables) => {
      await queryClient.invalidateQueries({ queryKey: taskKeys.attachments(variables.taskId) });
    },
  });
}

export function useUpdateTaskAttachmentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      attachmentId,
      originalFileName,
    }: {
      taskId: string;
      attachmentId: string;
      originalFileName: string;
    }) => taskService.updateAttachment(taskId, attachmentId, { originalFileName }),
    onSuccess: async (_attachment, variables) => {
      await queryClient.invalidateQueries({ queryKey: taskKeys.attachments(variables.taskId) });
    },
  });
}

export function useDeleteTaskAttachmentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, attachmentId }: { taskId: string; attachmentId: string }) =>
      taskService.deleteAttachment(taskId, attachmentId),
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({ queryKey: taskKeys.attachments(variables.taskId) });
    },
  });
}
